import { useCallback } from 'react';
import { HANDLE_COLLAPSE_EVENT_NAMES } from '@/consts/analytics.consts';
import { TrackTypeEnum, useAnalytics } from '@/hooks/useAnalytics';
import { useWorkflowStore } from '@/state/workflow.state';
import useWorkspacesStore from '@/state/workspaces.state';
import { FF_COLLAPSE_HANDLES_BY_TYPE } from '@/consts/featureFlags';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { log } from '@/logger/logger';
import type { HandleType } from '@/enums/handle-type.enum';

/** Reads the flag itself, so no event can describe behaviour the user could not have reached. */
export const useHandleCollapseAnalytics = ({ nodeId, nodeType }: { nodeId: string; nodeType: string | undefined }) => {
  const { track } = useAnalytics();
  const isFlagEnabled = useFeatureFlag(FF_COLLAPSE_HANDLES_BY_TYPE).value === true;
  const workflowId = useWorkflowStore((state) => state.recipe?.id);
  const workspaceId = useWorkspacesStore((state) => state.activeWorkspace.workspaceId);
  const workspaceName = useWorkspacesStore((state) => state.activeWorkspace.workspaceName);

  /**
   * Reporting must never take the interaction down with it. Both callers have already written the
   * new collapse state by the time they report it, so a throwing `track` would surface as a click
   * that half-worked.
   */
  const report = useCallback(
    (eventName: string, props: Record<string, unknown>) => {
      if (!isFlagEnabled) return;
      try {
        track(eventName, { nodeId, nodeType, workflowId, workspaceId, workspaceName, ...props }, TrackTypeEnum.Product);
      } catch (error) {
        log.warn(`Failed to report ${eventName}`, error);
      }
    },
    [isFlagEnabled, track, nodeId, nodeType, workflowId, workspaceId, workspaceName],
  );

  const trackModeToggled = useCallback(
    (props: { enabled: boolean; inputCount: number; groupCount: number; collapsibleInputCount: number }) =>
      report(HANDLE_COLLAPSE_EVENT_NAMES.MODE_TOGGLED, props),
    [report],
  );

  const trackGroupToggled = useCallback(
    (props: { handleType: HandleType; memberCount: number; expanded: boolean }) =>
      report(HANDLE_COLLAPSE_EVENT_NAMES.GROUP_TOGGLED, props),
    [report],
  );

  return { trackModeToggled, trackGroupToggled };
};
